import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import { VehicleDetail } from './details/VehicleDetail';
import { StationDetail } from './details/StationDetail';
import { MissionDetail } from './details/MissionDetail';
import type { Station, Vehicle } from '@/state/types';

interface DetailPanelProps {
  onRequestBuyVehicle: (station: Station) => void;
}

/**
 * Slide-in detail panel.
 *  - Mobile: full-screen bottom sheet.
 *  - Tablet: 50 % width from the right (auto-closes main panel).
 *  - Desktop: 420 px from the right.
 */
export function DetailPanel({ onRequestBuyVehicle }: DetailPanelProps) {
  const sel = useGameStore((s) => s.selectedDetail);
  const close = useGameStore((s) => s.clearDetail);
  const stations = useGameStore((s) => s.stations);
  const vehicles = useGameStore((s) => s.vehicles);
  const missions = useGameStore((s) => s.missions);
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const viewport = useViewport();

  let body: React.ReactNode = null;
  if (sel) {
    if (sel.kind === 'vehicle') {
      const v: Vehicle | undefined = vehicles.find((x) => x.id === sel.id);
      const station: Station | undefined = v ? stations.find((s) => s.id === v.stationId) : undefined;
      if (v && station) body = <VehicleDetail vehicle={v} station={station} onClose={close} />;
    } else if (sel.kind === 'station') {
      const st = stations.find((x) => x.id === sel.id);
      const stVehicles = st ? vehicles.filter((v) => v.stationId === st.id) : [];
      if (st)
        body = (
          <StationDetail
            station={st}
            vehicles={stVehicles}
            onClose={close}
            onRequestBuyVehicle={() => onRequestBuyVehicle(st)}
          />
        );
    } else if (sel.kind === 'mission') {
      const m = missions.find((x) => x.id === sel.id);
      if (m) body = <MissionDetail mission={m} onClose={close} />;
    }
  }

  const dur = reduceMotion ? 0.08 : 0.25;

  const variants =
    viewport === 'mobile'
      ? { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' } }
      : { initial: { x: '105%' }, animate: { x: 0 }, exit: { x: '105%' } };

  const positionClass =
    viewport === 'mobile'
      ? 'fixed inset-x-0 bottom-0 top-12 rounded-t-2xl'
      : viewport === 'tablet'
        ? 'fixed bottom-0 right-0 top-0 w-[50%] border-l border-border-strong'
        : 'fixed bottom-0 right-0 top-0 w-[420px] border-l border-border-strong';

  return (
    <AnimatePresence>
      {sel && body && (
        <motion.aside
          key="detail-panel"
          role="dialog"
          aria-label="Details"
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={{ duration: dur, ease: 'easeInOut' }}
          className={[
            'z-40 flex flex-col bg-bg-secondary shadow-2xl',
            positionClass,
          ].join(' ')}
        >
          {viewport === 'mobile' && (
            <div className="flex flex-col items-center pt-2 pb-1">
              <span className="h-1 w-10 rounded-full bg-fg-muted/40" aria-hidden />
            </div>
          )}
          {body}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
